import { parseRichTextSummary } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import type { DocumentPageMetadataUserEditValues } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { getCantileverDocumentPageId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { applyPageUserEditToSystemData } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import {
  EVAL_ONLY_PAGE_METADATA_LABELS,
  PAGE_METADATA_FIELD_CONFIG,
  type PageMetadataFieldLabel,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataFieldKeys'

export const SYSTEM_DOCUMENT_PAGE_ID_LABEL = 'System Document Page ID'
export const CANTILEVER_DOCUMENT_PAGE_ENTRY_ID_LABEL =
  'Cantilever Document Page Entry ID'

export type MetadataDisplayField = {
  label: string
  value: string
}

export type MetadataInternalIdsOptions = {
  includeInternalIds?: boolean
}

export type MetadataCardField = {
  label: string
  value: string | null
  /** Span both columns in the sidebar card grid (long IDs). */
  fullWidth?: boolean
}

function nonEmpty(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function pushIdField(
  fields: MetadataDisplayField[],
  label: string,
  value: string | null | undefined
): void {
  const trimmed = nonEmpty(value)
  if (trimmed) {
    fields.push({ label, value: trimmed })
  }
}

/** Gecko-internal Rolodex/cantilever page IDs; omit from customer-facing metadata. */
export function getInternalPageIdDisplayFields(ids: {
  systemDocumentPageId?: string | null
  cantileverDocumentPageEntryId?: string | null
}): MetadataDisplayField[] {
  const fields: MetadataDisplayField[] = []
  pushIdField(fields, SYSTEM_DOCUMENT_PAGE_ID_LABEL, ids.systemDocumentPageId)
  pushIdField(
    fields,
    CANTILEVER_DOCUMENT_PAGE_ENTRY_ID_LABEL,
    ids.cantileverDocumentPageEntryId
  )
  return fields
}

function getPageMetadataFields(
  page: SystemDocumentPageEntry | undefined,
  pageNum: number,
  userEditValues?: DocumentPageMetadataUserEditValues | null
): MetadataCardField[] {
  const data = applyPageUserEditToSystemData(
    page?.entry.data,
    userEditValues ?? undefined
  )
  return PAGE_METADATA_FIELD_CONFIG.map((field) => {
    if (!('key' in field)) {
      return { label: field.label, value: String(pageNum) }
    }
    return {
      label: field.label,
      value: nonEmpty(data?.[field.key]),
    }
  })
}

export type PageMetadataDisplayOptions = MetadataInternalIdsOptions & {
  keepEmptyLabel?: PageMetadataFieldLabel
}

/**
 * Preview list: omit empty values (John's style), except `keepEmptyLabel`,
 * which stays in place with an empty value so the UI can show "Not set".
 */
export function getPageMetadataDisplayFields(
  page: SystemDocumentPageEntry | undefined,
  pageNum: number,
  userEditValues?: DocumentPageMetadataUserEditValues | null,
  options?: PageMetadataDisplayOptions
): MetadataDisplayField[] {
  const fields = getPageMetadataFields(page, pageNum, userEditValues).flatMap(
    ({ label, value }) =>
      EVAL_ONLY_PAGE_METADATA_LABELS.has(label) ||
      !(value || label === options?.keepEmptyLabel)
        ? []
        : [{ label, value: value ?? '' }]
  )
  if (!options?.includeInternalIds) return fields
  return [
    ...fields,
    ...getInternalPageIdDisplayFields({
      systemDocumentPageId: page?.entry.id,
      cantileverDocumentPageEntryId: page
        ? getCantileverDocumentPageId(page)
        : undefined,
    }),
  ]
}

/** Sidebar cards: keep empties so UI can show "Not set". */
export function getPageMetadataCardFields(
  page: SystemDocumentPageEntry | undefined,
  pageNum: number,
  userEditValues?: DocumentPageMetadataUserEditValues | null
): MetadataCardField[] {
  return getPageMetadataFields(page, pageNum, userEditValues)
}

export function hasPageMetadataBeyondPageNumber(
  fields: readonly MetadataDisplayField[]
): boolean {
  return fields.some((field) => field.label !== 'Page' && field.value !== '')
}

/**
 * Parse page rich_text_summary the same way as document rows: summary text
 * before `@@@`, comma-separated keywords after.
 */
export function getPageParsedRichText(
  page: SystemDocumentPageEntry | undefined
): {
  summary: string
  keywords: string[]
} {
  const raw = page?.entry.data.rich_text_summary
  const { summary, keywords } = parseRichTextSummary(
    typeof raw === 'string' ? raw : undefined
  )
  return { summary: summary ?? '', keywords }
}

/** Display-ready page summary with the keyword section removed. */
export function getPageRichTextSummary(
  page: SystemDocumentPageEntry | undefined
): string {
  return getPageParsedRichText(page).summary
}
